import { dlog, dLogTime, dLogTimeEnd } from "@shared/utility.js";
import { getJWTTokenURL, getUploadURLURL, waitImageUploadURL } from "@shared/environment.js";

const cfAccountHash = "Y1aaHxnza6mSWrSp8Ze_dw";

async function fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
        if (error.name === "AbortError") {
            throw new Error('Request timed out. Please check your connection and try again.');
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
    }
}

async function imageToBlob(imageElement) {
    if (imageElement instanceof HTMLCanvasElement) {
        return new Promise((resolve, reject) => {
            imageElement.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Failed to convert canvas to blob"));
                }
            }, "image/png");
        });
    } else if (imageElement instanceof HTMLImageElement) {
        const canvas = document.createElement("canvas");
        canvas.width = imageElement.naturalWidth || imageElement.width;
        canvas.height = imageElement.naturalHeight || imageElement.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imageElement, 0, 0);

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Failed to convert image to blob"));
                }
            }, "image/png");
        });
    } else {
        throw new Error("Unsupported image type");
    }
}

async function fetchCFImage(imageID, width, format="webp", quality=100, dpr=1) {
    const cfUrl = `https://imagedelivery.net/${cfAccountHash}/${imageID}/w=${width},format=${format},quality=${quality},dpr=${dpr}`;

    dLogTime(`fetchCFImage: ${cfUrl}`);

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = cfUrl;
    
        img.onload = async () => {
            try {
                await img.decode();
                dLogTimeEnd(`fetchCFImage: ${cfUrl}`);
                resolve(img);
            } catch (error) {
                reject(error);
            }
        };
    
        img.onerror = (error) => {
            reject(error);
        };
    });
}

async function uploadImageToCloudflare(imageElement) {
    // Step 1: Get JWT token
    const tokenResponse = await fetchWithTimeout(getJWTTokenURL(), {
        method: "GET"
    }, 15000);

    if (!tokenResponse.ok) {
        throw new Error(`Failed to get JWT token: ${tokenResponse.status} ${tokenResponse.statusText}`);
    }

    const tokenData = await tokenResponse.json();
    const token = tokenData.token;

    // Step 2: Get upload URL
    const uploadURLResponse = await fetchWithTimeout(getUploadURLURL(), {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    }, 15000);

    if (!uploadURLResponse.ok) {
        const errorText = await uploadURLResponse.text();
        throw new Error(`Failed to get upload URL: ${uploadURLResponse.status} ${errorText}`);
    }

    const uploadData = await uploadURLResponse.json();
    const { uploadURL, id } = uploadData.result;

    // Step 3: Upload image to Cloudflare
    const imgBlob = await imageToBlob(imageElement);
    const formData = new FormData();
    formData.append("file", imgBlob);

    const uploadResponse = await fetchWithTimeout(uploadURL, {
        method: "POST",
        body: formData
    }, 60000);

    if (!uploadResponse.ok) {
        throw new Error(`Failed to upload image: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    // Step 4: Get fresh JWT token (previous may have expired)
    const freshTokenResponse = await fetchWithTimeout(getJWTTokenURL(), {
        method: "GET"
    }, 15000);

    if (!freshTokenResponse.ok) {
        throw new Error(`Failed to get fresh JWT token: ${freshTokenResponse.status} ${freshTokenResponse.statusText}`);
    }

    const freshTokenData = await freshTokenResponse.json();
    const freshToken = freshTokenData.token;

    // Step 5: Wait for image processing
    const waitResponse = await fetchWithTimeout(waitImageUploadURL(), {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${freshToken}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ id })
    }, 30000);

    if (!waitResponse.ok) {
        const errorText = await waitResponse.text();
        throw new Error(`Failed to wait for image upload: ${waitResponse.status} ${errorText}`);
    }

    const waitData = await waitResponse.json();
    const { baseURL, imageID } = waitData;

    return { baseURL, imageID };
}

export { cfAccountHash, fetchCFImage, uploadImageToCloudflare };